import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, ShieldCheck, Upload, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';
import { AddressFieldsAutosuggest } from './auth/AddressFieldsAutosuggest';

const API = process.env.REACT_APP_BACKEND_URL;

/** Landing page opened from the batch-member welcome email. Verifies the
 *  token, then lets the user set a password + fill missing profile fields
 *  (birthdate, address, ID photo, selfie). Public — no auth needed. */
export function CompleteProfilePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [status, setStatus] = useState('verifying'); // verifying | ready | done | invalid
  const [greet, setGreet] = useState({ email: '', name: '' });
  const [busy, setBusy] = useState(false);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [address, setAddress] = useState({ line1: '', city: '', state: '', country: '', postal_code: '' });
  const [idPhoto, setIdPhoto] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [idPhotoUrl, setIdPhotoUrl] = useState('');
  const [selfieUrl, setSelfieUrl] = useState('');
  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingSelfie, setUploadingSelfie] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    axios.get(`${API}/api/auth/complete-profile/verify`, { params: { token } })
      .then(({ data }) => {
        setGreet({ email: data.email, name: data.name || '' });
        setStatus('ready');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const uploadFile = async (file, purpose, setUrl, setUploading) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please pick an image file.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', purpose);
      // Public upload uses the token itself as bearer via a header the
      // backend accepts (member-file endpoint is auth'd; for this flow
      // we ship the file inline in the final submit instead).
      // Simplest: upload via the same public complete-profile-upload endpoint.
      const { data } = await axios.post(`${API}/api/auth/complete-profile/upload?token=${encodeURIComponent(token)}&purpose=${purpose}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUrl(data.url);
      toast.success(`${purpose === 'id-photo' ? 'ID' : 'Selfie'} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const submit = async () => {
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/complete-profile`, {
        token,
        password,
        birthdate: birthdate || null,
        address_line1: address.line1 || null,
        city: address.city || null,
        state: address.state || null,
        country: address.country || null,
        postal_code: address.postal_code || null,
        id_photo_url: idPhotoUrl || null,
        selfie_url: selfieUrl || null,
      });
      setStatus('done');
      toast.success('Profile completed. You can now log in!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete profile');
    }
    setBusy(false);
  };

  if (status === 'verifying') {
    return <FullPageState><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" /><p className="text-[#A0B4CC] mt-3 text-sm">Verifying your link…</p></FullPageState>;
  }
  if (status === 'invalid') {
    return (
      <FullPageState>
        <X className="w-10 h-10 text-[#FF6B6B]" />
        <p className="text-white text-lg mt-3">This link is invalid or has expired.</p>
        <p className="text-[#A0B4CC] text-sm mt-1">Please contact our support team for a fresh invitation.</p>
      </FullPageState>
    );
  }
  if (status === 'done') {
    return (
      <FullPageState>
        <CheckCircle2 className="w-12 h-12 text-[#00E676]" />
        <p className="text-white text-lg mt-3">All set, {greet.name || 'welcome'}!</p>
        <Button className="mt-6 bg-[#00E5FF] text-[#001627]" onClick={() => navigate('/login')} data-testid="complete-goto-login">
          Sign in
        </Button>
      </FullPageState>
    );
  }

  return (
    <div className="min-h-screen bg-[#000B18] p-4 sm:p-6 lg:p-8">
      <div className="max-w-2xl mx-auto">
        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#00E5FF]" />
              Complete your profile
            </CardTitle>
            <p className="text-[#A0B4CC] text-xs mt-1">
              Hi {greet.name || greet.email}! Set a password and finish your details to sign in.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-white text-xs">New password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#000B18] border-white/20 text-white mt-1" data-testid="cp-password" />
              </div>
              <div>
                <Label className="text-white text-xs">Confirm password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[#000B18] border-white/20 text-white mt-1" data-testid="cp-confirm" />
              </div>
            </div>
            <div>
              <Label className="text-white text-xs">Birthdate (optional)</Label>
              <Input type="date" value={birthdate} onChange={(e) => setBirthdate(e.target.value)} className="bg-[#000B18] border-white/20 text-white mt-1 max-w-xs" data-testid="cp-birthdate" />
            </div>

            <AddressFieldsAutosuggest value={address} onChange={setAddress} required={false} />

            <UploadRow
              label="ID photo (optional)"
              testId="cp-id-photo"
              file={idPhoto}
              url={idPhotoUrl}
              uploading={uploadingId}
              onPick={(f) => { setIdPhoto(f); uploadFile(f, 'id-photo', setIdPhotoUrl, setUploadingId); }}
              onClear={() => { setIdPhoto(null); setIdPhotoUrl(''); }}
            />
            <UploadRow
              label="Selfie (optional)"
              testId="cp-selfie"
              file={selfie}
              url={selfieUrl}
              uploading={uploadingSelfie}
              onPick={(f) => { setSelfie(f); uploadFile(f, 'selfie', setSelfieUrl, setUploadingSelfie); }}
              onClear={() => { setSelfie(null); setSelfieUrl(''); }}
            />

            <Button
              onClick={submit}
              disabled={busy || !password || password !== confirm}
              className="bg-[#00E5FF] text-[#001627] hover:bg-[#00B8D4] w-full"
              data-testid="cp-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Complete profile
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FullPageState({ children }) {
  return (
    <div className="min-h-screen bg-[#000B18] flex items-center justify-center p-6">
      <div className="text-center max-w-md">{children}</div>
    </div>
  );
}

function UploadRow({ label, testId, file, url, uploading, onPick, onClear }) {
  return (
    <div className="rounded-md border border-white/10 bg-[#000B18]/40 p-3">
      <Label className="text-white text-xs">{label}</Label>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <input type="file" accept="image/*" onChange={(e) => onPick(e.target.files?.[0])} className="text-xs text-[#A0B4CC] file:bg-[#00E5FF]/15 file:text-[#00E5FF] file:border-0 file:rounded file:px-2 file:py-1 file:mr-2" data-testid={`${testId}-input`} />
        {uploading && <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" />}
        {url && !uploading && (
          <>
            <CheckCircle2 className="w-4 h-4 text-[#00E676]" />
            <button onClick={onClear} className="text-[#FF6B6B] text-xs" data-testid={`${testId}-clear`}><X className="w-3 h-3 inline" /> clear</button>
          </>
        )}
      </div>
    </div>
  );
}

export default CompleteProfilePage;
